import React from "react";
import { getOmpSearchString } from "@src/api/main/OMPTypes";
import { enterpriseApi, type Enterprise, type EnterpriseFilter } from "@src/api/dictionaries/enterprise";
import { formatEnterpriseText } from "@src/components/business-data-display";
import { BusinessSearchSelect, type BusinessSearchSelectProps } from "../BusinessSearchSelect";
import { PickerOptionCard } from "@src/shared/ui";

export type EnterpriseSearchSelectProps = Omit<
    BusinessSearchSelectProps<Enterprise, number>,
    "queryKeyBase" | "loadOptions" | "loadById" | "getId" | "getOptionLabel" | "getSelectedLabel"
> & {
    /** Дополнительный фильтр API предприятий. */
    flt?: EnterpriseFilter;
};

/** SearchSelect для одиночного выбора предприятия-контрагента. */
export function EnterpriseSearchSelect({
    label = "Предприятие",
    flt,
    minSearchLength = 2,
    debounceDelay = 500,
    searchPlaceholder = "Наименование или код предприятия",
    value,
    onChange,
    optionSlots,
    ...rest
}: EnterpriseSearchSelectProps) {
    return (
        <BusinessSearchSelect<Enterprise, number>
            queryKeyBase={["searchSelect", "enterprise", flt]}
            loadOptions={async (query, signal) => {
                return enterpriseApi.load(
                    {
                        flt: {
                            ...flt,
                            Name: getOmpSearchString(query, { fullTextSearch: true }),
                        },
                    },
                    { signal },
                );
            }}
            loadById={async (code, signal) => {
                const items = await enterpriseApi.load({ flt: { ...flt, Codes: [code] } }, { signal });
                return items[0] ?? null;
            }}
            getId={(item) => item.Code}
            getOptionLabel={(item) => formatEnterpriseText(item)}
            getSelectedLabel={(item) => formatEnterpriseText(item)}
            value={value}
            onChange={onChange}
            label={label}
            searchPlaceholder={searchPlaceholder}
            minSearchLength={minSearchLength}
            debounceDelay={debounceDelay}
            {...rest}
            optionSlots={{
                ...optionSlots,
                fullscreen:
                    optionSlots?.fullscreen ??
                    ((item) => {
                        return (
                            <PickerOptionCard
                                title={`Предприятие ${formatEnterpriseText(item)}`}
                                details={[
                                    { label: "Наименование", value: item.Name },
                                    { label: "Заводской код", value: item.WsCode },
                                ]}
                            />
                        );
                    }),
            }}
        />
    );
}
